
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Bot } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";

interface Message {
  id: string;
  content: string;
  is_from_user: boolean;
  created_at: string;
  type: string;
  image_url?: string;
}

interface ChatMessageProps {
  message: Message;
}

interface MathBlock {
  type: 'display' | 'inline';
  tex: string;
}

const formatMessageContent = (content: string) => {
  // Remove quotes from start and end of message
  let formattedContent = content.replace(/^["']|["']$/g, '');

  // Process display math mode with \[ \] first, before splitting into lines
  const mathBlocks: MathBlock[] = [];
  
  // Handle display math mode with \[ \] - using [\s\S]*? to match any character including newlines
  formattedContent = formattedContent.replace(/\\\[([\s\S]*?)\\\]/g, (match, tex) => {
    mathBlocks.push({ type: 'display', tex: tex.trim() });
    return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`;
  });

  // Split content by newlines and process each line
  const lines = formattedContent.split('\n');

  return lines.map(line => {
    // Process headers (# through ####) with potential bold text
    if (line.startsWith('#### ')) {
      const content = line.substring(5);
      const boldProcessed = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return `<h4 class="text-base font-semibold mt-3 mb-2">${boldProcessed}</h4>`;
    }
    if (line.startsWith('### ')) {
      const content = line.substring(4);
      const boldProcessed = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return `<h3 class="text-lg font-semibold mt-4 mb-2">${boldProcessed}</h3>`;
    }
    if (line.startsWith('## ')) {
      const content = line.substring(3);
      const boldProcessed = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return `<h2 class="text-xl font-bold mt-6 mb-3">${boldProcessed}</h2>`;
    }
    if (line.startsWith('# ')) {
      const content = line.substring(2);
      const boldProcessed = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return `<h1 class="text-2xl font-bold mt-8 mb-4">${boldProcessed}</h1>`;
    }

    // Process URLs
    line = line.replace(/(https?:\/\/[^\s]+)/g, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${url}</a>`;
    });

    // Process bold text
    line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Handle inline math mode with \( \)
    line = line.replace(/\\\((.*?)\\\)/g, (match, tex) => {
      mathBlocks.push({ type: 'inline', tex: tex.trim() });
      return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`;
    });

    // Replace math block placeholders with rendered KaTeX
    mathBlocks.forEach((block, index) => {
      try {
        const rendered = katex.renderToString(block.tex, {
          displayMode: block.type === 'display',
          throwOnError: false,
          strict: false,
          trust: true,
          macros: {
            "\\dx": "\\,dx",
            "\\F": "F",
          }
        });
        line = line.replace(
          `%%MATH_BLOCK_${index}%%`,
          block.type === 'display'
            ? `<div class="my-2 text-center">${rendered}</div>`
            : `<span class="inline-math">${rendered}</span>`
        );
      } catch (error) {
        console.error('KaTeX rendering error:', error);
        line = line.replace(
          `%%MATH_BLOCK_${index}%%`,
          `<span class="text-red-500">[Math Error: ${error.message}]</span>`
        );
      }
    });

    return `<div class="min-h-[20px]">${line}</div>`;
  }).join(''); // Join without newlines since we're using div wrappers
};

export function ChatMessage({ message }: ChatMessageProps) {
  const formattedContent = formatMessageContent(message.content);

  return (
    <div
      className={cn(
        "flex gap-3 w-full max-w-screen-md mx-auto",
        message.is_from_user ? "flex-row-reverse" : "flex-row"
      )}
    >
      {!message.is_from_user && (
        <Avatar className="h-8 w-8">
          <AvatarImage src="/lovable-uploads/37369634-e5ff-4200-b7aa-0ece5a8baa04.png" alt="Bot" />
          <AvatarFallback>
            <Bot className="h-5 w-5" />
          </AvatarFallback>
        </Avatar>
      )}
      <div
        className={cn(
          "rounded-lg px-3 py-2 max-w-[85%] break-words",
          message.is_from_user
            ? "bg-primary text-primary-foreground"
            : "bg-muted"
        )}
      >
        {message.image_url && (
          <div className="mb-2">
            <img 
              src={message.image_url} 
              alt="Attached" 
              className="max-w-full rounded-lg"
              style={{ maxHeight: '200px', objectFit: 'contain' }}
            />
          </div>
        )}
        <div 
          dangerouslySetInnerHTML={{ __html: formattedContent }}
        />
      </div>
    </div>
  );
}
